import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import BrandHeader from "../components/BrandHeader";
import ElementBadge from "../components/ElementBadge";
import LotusLoader from "../components/LotusLoader";
import { ordersApi, paymentsApi, quizApi } from "../lib/api";
import { PRODUCT } from "../lib/constants";
import { titleCaseName, razorpayContactFromPhone } from "../lib/text";
import { useSEO } from "../lib/seo";
import { crystalColor } from "../lib/crystalColors";
import { ELEMENT_GLYPH, ELEMENTS } from "../lib/panchaEngine";

const RAZORPAY_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (typeof window === "undefined") return resolve(false);
    if (window.Razorpay) return resolve(true);
    const existing = document.querySelector(`script[src="${RAZORPAY_SCRIPT}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () => resolve(false));
      return;
    }
    const s = document.createElement("script");
    s.src = RAZORPAY_SCRIPT;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function PrescriptionReveal() {
  useSEO({
    title: "Your Sacred Report | Pancha Healing",
    description: "Your personalised crystal prescription and Sacred Report.",
    noIndex: true,
  });
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  // If Razorpay checkout is closed without success or fails, show a friendly retry banner
  const [paymentFailed, setPaymentFailed] = useState(false);

  useEffect(() => {
    ordersApi.get(id).then((o) => {
      // Edge case: returning paid customer who didn't submit address yet → send to address form.
      // Already-confirmed orders with address → confirmation page.
      if (o.paymentStatus === "Payment Confirmed") {
        if (o.deliveryAddress) {
          navigate(`/order/${id}/confirmation`, { replace: true });
        } else {
          navigate(`/order/${id}/address`, { replace: true });
        }
        return;
      }
      setOrder(o);
      setLoading(false);
      // Mark that this lead reached the payment page
      quizApi.reachedPayment(id).catch(() => {});
    })
      .catch(() => { setLoading(false); });
    // Pre-load Razorpay checkout script so the first click opens instantly
    loadRazorpay();
  }, [id, navigate]);

  if (loading) return <LotusLoader context="general" />;
  if (!order) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-3">
      <p className="text-[#6B5E7E]">Prescription not found.</p>
      <Link to="/" className="ph-btn-primary">Back home</Link>
    </div>
  );
  // Incomplete lead (abandoned earlier) — send them back to the wizard
  if (!order.numerology || !order.panchaBeads || !order.prescriptionCrystals) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 px-6 text-center">
        <p className="text-[#6B5E7E] max-w-md">Your prescription is still being completed. Let&apos;s pick up where you left off.</p>
        <Link to="/prescription" className="ph-btn-primary">Continue my reading</Link>
      </div>
    );
  }

  const { numerology, panchaBeads, prescriptionCrystals, dominantElement, fullName } = order;
  const firstName = titleCaseName((fullName || "").trim().split(/\s+/)[0]) || "there";
  const totalCrystals = prescriptionCrystals.length;

  const handleRazorpayCheckout = async () => {
    setPaymentFailed(false);
    // Stable event IDs so Meta can deduplicate our browser pixel against any
    // server-side / CAPI / gateway-injected duplicate of the same funnel step.
    // Meta only dedups when BOTH pixel + CAPI events share the same event_id;
    // sending our correct value=1599 with a stable ID makes it the source of
    // truth if a duplicate ever arrives with a wrong value.
    const orderRef = order.id;
    const evtInit = `ic_${orderRef}`;
    const evtAdd  = `ap_${orderRef}`;
    const evtPur  = `pu_${orderRef}`;
    try {
      // Meta Pixel — checkout initiated
      if (typeof window !== "undefined" && typeof window.fbq === "function") {
        window.fbq("track", "InitiateCheckout", {
          value: Number(PRODUCT.price),
          currency: "INR",
          num_items: 1,
          content_name: "Sacred Crystal Bracelet",
          content_type: "product",
        }, { eventID: evtInit });
      }
      const ok = await loadRazorpay();
      if (!ok || !window.Razorpay) {
        toast.error("Could not load payment gateway. Please check your connection.");
        return;
      }
      const rz = await paymentsApi.createOrder(order.id);
      const options = {
        key: rz.key_id,
        amount: rz.amount,
        currency: rz.currency,
        name: "Pancha Healing",
        description: PRODUCT.name,
        image: "/logo192.png",
        order_id: rz.rz_order_id,
        // Skip returning-customer lookup so first-time buyers don't see the
        // extra "Continue with existing account" flash before payment tiles.
        remember_customer: false,
        prefill: {
          name: rz.name || order.fullName || "",
          email: rz.email || order.email || "",
          // Razorpay reliably prefills the mobile field only when it receives
          // a bare 10-digit Indian mobile. `+91XXXXXXXXXX` sometimes leaves
          // the field blank — normalise here to avoid the extra contact step.
          contact: razorpayContactFromPhone(rz.contact || order.whatsapp || ""),
        },
        notes: { orderNumber: rz.orderNumber || order.orderNumber || "" },
        theme: { color: "#6A0DAD" },
        modal: {
          ondismiss: () => {
            // User closed the Razorpay modal without completing payment.
            // Quiz data is safe (persisted in DB) — surface a friendly retry banner.
            setPaymentFailed(true);
          },
        },
        handler: async (response) => {
          setConfirming(true);
          try {
            await paymentsApi.verify({
              order_id: order.id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            if (typeof window !== "undefined" && typeof window.fbq === "function") {
              window.fbq("track", "Purchase", {
                value: Number(PRODUCT.price),
                currency: "INR",
                content_name: "Sacred Crystal Bracelet",
                content_ids: ["sacred-crystal-bracelet"],
                content_type: "product",
                num_items: 1,
              }, { eventID: evtPur });
            }
            setConfirming(false);
            // Route to delivery-address form; new activated-success page renders after address is submitted.
            navigate(`/order/${order.id}/address`, { replace: true });
          } catch (e) {
            setConfirming(false);
            setPaymentFailed(true);
            toast.error("Payment verification failed. Please try again or WhatsApp us.");
          }
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        setPaymentFailed(true);
        toast.error(resp?.error?.description || "Payment failed. Please try again.");
      });
      // Meta Pixel — mid-funnel event fired when we hand off to Razorpay. Gives
      // us an "opened Razorpay" cohort to compare against InitiateCheckout and
      // Purchase, so we can see WHERE customers drop off.
      if (typeof window !== "undefined" && typeof window.fbq === "function") {
        window.fbq("track", "AddPaymentInfo", {
          value: Number(PRODUCT.price),
          currency: "INR",
          content_name: "Sacred Crystal Bracelet",
          content_type: "product",
        }, { eventID: evtAdd });
      }
      rzp.open();
    } catch (e) {
      setPaymentFailed(true);
      toast.error("Unable to start payment. Please try again in a moment.");
    }
  };

  if (confirming) {
    return <LotusLoader context="payment" />;
  }

  return (
    <div className="min-h-screen" data-testid="reveal-page">
      <BrandHeader />

      <div className="max-w-5xl mx-auto px-5 md:px-10 py-12">
        {/* HEADER */}
        <div className="text-center fade-up">
          <div className="ph-overline">A glimpse of your prescription</div>
          <h1 className="font-serif text-4xl md:text-6xl text-[#2C0A5E] mt-3">Hello, {firstName}.</h1>
          <p className="text-[#6B5E7E] mt-3 max-w-2xl mx-auto">The stones the universe chose for you are ready. Here is what your soul already knows.</p>

          <div className="flex flex-wrap items-center justify-center gap-2 mt-7">
            <span className="ph-badge" data-testid="badge-lifepath">Life Path <strong className="ml-1 text-[#6A0DAD]">{numerology.lifePath}</strong></span>
            <span className="ph-badge" data-testid="badge-namenum">Name Number <strong className="ml-1 text-[#6A0DAD]">{numerology.nameNumber}</strong></span>
            <span className="ph-badge" data-testid="badge-zodiac">Zodiac <strong className="ml-1 text-[#6A0DAD]">{numerology.zodiac}</strong></span>
            <ElementBadge element={dominantElement}>{ELEMENT_GLYPH[dominantElement]} Dominant {ELEMENTS[dominantElement].name}</ElementBadge>
          </div>
        </div>

        {/* PANCHA FOUNDATION - NAMES BLURRED */}
        <section className="mt-14 fade-up delay-200">
          <SectionTitle eyebrow="Pancha Foundation" title="Your five element beads" />
          <p className="text-[#6B5E7E] mt-3 max-w-3xl">
            Your dominant element is <strong className="text-[#6A0DAD]">{ELEMENTS[dominantElement].name} ({ELEMENTS[dominantElement].sanskrit})</strong>. All five Pancha crystals have been selected to balance with your {ELEMENTS[dominantElement].name} frequency.
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mt-7">
            {panchaBeads.map((b) => (
              <div key={b.element} className="ph-card p-5 flex flex-col items-center text-center relative" data-testid={`glimpse-pancha-${b.element}`}>
                <div className="w-14 h-14 rounded-full bead" style={{ backgroundColor: crystalColor(b.crystal) }} />
                <div className="ph-overline mt-3">{b.element}</div>
                <div className="font-serif text-lg text-[#2C0A5E] mt-1">{ELEMENTS[b.element].sanskrit}</div>
                <div className="text-sm text-[#2C0A5E] mt-1 inline-flex items-center gap-1.5">
                  <span>{b.crystal}</span>
                  <span aria-label="locked reason" title="Reason unlocks after payment" style={{ color: "#D4A855", fontSize: "11px" }}>&#128274;</span>
                </div>
                <div className="mt-3 text-[11px] tracking-wider text-[#D4A855] uppercase">8mm</div>
              </div>
            ))}
          </div>
        </section>

        {/* PRESCRIPTION - ALL crystals visible by NAME; reasons blurred behind gold lock */}
        <section className="mt-14 fade-up delay-300">
          <SectionTitle eyebrow="Your prescription" title={`${totalCrystals} Crystals Selected`} />
          <p className="text-[#6B5E7E] mt-3 max-w-3xl">Each stone below is chosen for you. The reason each one was selected unlocks after payment.</p>
          <div className="grid sm:grid-cols-2 gap-4 mt-7">
            {prescriptionCrystals.map((c, i) => (
              <div key={`${c.crystal}-${i}`} className="ph-card p-5 flex gap-4" data-testid={`glimpse-rx-${i}`}>
                <div className="shrink-0 flex flex-col items-center gap-1">
                  <div className="w-12 h-12 rounded-full bead" style={{ backgroundColor: crystalColor(c.crystal) }} />
                </div>
                <div className="flex-1">
                  <div className="font-serif text-lg text-[#2C0A5E] inline-flex items-center gap-1.5">
                    <span>{c.crystal}</span>
                    <span aria-label="locked reason" title="Reason unlocks after payment" style={{ color: "#D4A855", fontSize: "12px" }}>&#128274;</span>
                  </div>
                  <div
                    className="text-[11px] tracking-wider uppercase text-[#D4A855] mt-0.5 select-none"
                    style={{ filter: "blur(5px)", userSelect: "none" }}
                    aria-hidden
                  >
                    {c.source}
                  </div>
                  <p className="text-sm text-[#6B5E7E] mt-1.5 leading-relaxed select-none" style={{ filter: "blur(5px)", userSelect: "none" }} aria-hidden>
                    {c.reason}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* LOCK BOX */}
        <section className="mt-14 fade-up">
          <div className="rounded-2xl p-8 md:p-10 text-[#F4E8C1]" style={{ background: "linear-gradient(135deg, #2C0A5E 0%, #6A0DAD 100%)" }} data-testid="lock-box">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#D4A855]/20 text-[#D4A855]">&#128274;</span>
              <div className="ph-overline" style={{ color: "#D4A855" }}>Locked</div>
            </div>
            <h2 className="font-display text-3xl md:text-4xl text-white mt-4">Everything written in your stars &mdash; revealed after payment.</h2>
            <p className="font-serif italic mt-3 max-w-xl" style={{ color: "#FAF7F2" }}>Delivered personally to your WhatsApp within 2 hours. Every detail chosen for you alone.</p>

            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5 mt-6 text-sm text-[#f4e8c1]">
              {[
                "The healing meaning behind each bead",
                "All " + totalCrystals + " prescription crystals with full reasons",
                "Exact bead arrangement for your bracelet",
                "Sacred Blueprint grid (Lucky Color, Day, Numbers, Ruling Planet)",
                "Complete numerology report",
                "Karma lesson for this lifetime",
                "Sacred guidance - dos and donts",
                "Your personal affirmation",
              ].map((item) => (
                <div key={item} className="flex items-start gap-2">
                  <span className="mt-1.5 w-1 h-1 rounded-full shrink-0 bg-[#D4A855]" />
                  <span>{item}</span>
                </div>
              ))}
            </div>

            {/* FIX 6 — urgency line at bottom of locked features list */}
            <p
              className="text-center font-serif italic mt-6"
              style={{ color: "#D4A855", fontSize: "14px" }}
              data-testid="lock-box-urgency"
            >
              <span aria-hidden>&#10022;</span> Complete your order to activate your prescription
            </p>

            {/* FOMO — reservation expiry + immutable numbers */}
            <p
              className="text-center font-serif italic mt-3"
              style={{ color: "#FAF7F2", fontSize: "13px", opacity: 0.85 }}
              data-testid="lock-box-fomo"
            >
              Your prescription expires from reservation in 24 hours. Your numbers don&rsquo;t change &mdash; but this moment does.
            </p>
          </div>
        </section>

        {/* TRUST BAR - just above payment */}
        <section className="mt-12 fade-up">
          <div className="rounded-2xl bg-[#F1ECE2]/70 border border-[rgba(212,168,85,0.25)] px-5 py-4" data-testid="trust-bar">
            <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-sm text-[#2C0A5E]">
              <div className="inline-flex items-center gap-2">
                <span className="text-base" style={{ color: "#D4A855" }} aria-hidden>&#128274;</span>
                <span>100% Secure Payment</span>
              </div>
              <div className="inline-flex items-center gap-2">
                <span className="text-base" style={{ color: "#D4A855" }} aria-hidden>&#128666;</span>
                <span>Ships Across India</span>
              </div>
              <div className="inline-flex items-center gap-2">
                <span className="text-base" style={{ color: "#D4A855" }} aria-hidden>&#10024;</span>
                <span>Energized &amp; Handcrafted</span>
              </div>
            </div>
          </div>
        </section>

        {/* PAYMENT SECTION */}
        <section className="mt-6 fade-up">
          <div className="ph-card p-7 md:p-10">
            <div className="text-center">
              <div className="ph-overline">Complete your order</div>
              <div className="font-serif text-6xl md:text-7xl text-[#6A0DAD] mt-3" data-testid="price-display">Rs {PRODUCT.price}</div>
              <p className="font-serif text-xl text-[#2C0A5E] mt-1">{PRODUCT.name}</p>
            </div>

            <ul className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-2 max-w-xl mx-auto text-sm text-[#2C0A5E]">
              {PRODUCT.includes.map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 bg-[#D4A855]" />
                  {item}
                </li>
              ))}
            </ul>

            {/* Guarantee line - above Razorpay button */}
            <p className="mt-8 text-center text-xs text-[#9e94ae]" data-testid="guarantee-line">
              <span style={{ color: "#D4A855" }} aria-hidden>&#128155;</span>{" "}
              Not satisfied? Full refund within 7 days &mdash; no questions asked
            </p>

            {/* International order note (non-India phone numbers) */}
            {order.whatsapp && !order.whatsapp.replace(/\s/g, "").startsWith("+91") && (
              <p
                className="text-center font-serif italic mt-4"
                style={{ color: "#D4A855", fontSize: "13px" }}
                data-testid="intl-order-note"
              >
                <span aria-hidden>🌍</span> International orders welcome. Shipping calculated on confirmation. Estimated delivery 7-15 days.
              </p>
            )}

            {/* Value reframe line above CTA (preserved from prior copy) */}
            <p
              className="text-center font-serif italic"
              style={{ color: "#D4A855", fontSize: "14px", marginTop: "14px" }}
              data-testid="cta-value-line"
            >
              Rs {PRODUCT.price} &mdash; the cost of one meal out. The value of knowing yourself.
            </p>

            {/* FIX 5 — social-proof testimonial ABOVE the pay button */}
            <div
              className="text-center mt-5"
              data-testid="checkout-social-proof"
            >
              <div aria-hidden style={{ color: "#D4A855", letterSpacing: "3px", fontSize: "13px" }}>&#9733;&#9733;&#9733;&#9733;&#9733;</div>
              <p
                className="font-serif italic mt-1"
                style={{ color: "#2C0A5E", fontSize: "13px" }}
              >
                &ldquo;I felt this was written only for me.&rdquo; &mdash; Priya S., Mumbai
              </p>
            </div>

            {/* FIX 2 — retry banner for failed/cancelled Razorpay checkout */}
            {paymentFailed && (
              <div
                className="mt-5 rounded-xl px-4 py-3 text-center"
                style={{ background: "#FFF4E5", border: "1px solid #F1C889" }}
                data-testid="payment-failed-banner"
              >
                <p className="text-sm" style={{ color: "#8A4B00" }}>
                  Payment didn&rsquo;t complete. Your prescription is still reserved.
                </p>
                <button
                  type="button"
                  onClick={handleRazorpayCheckout}
                  className="mt-2 text-xs font-semibold tracking-wide uppercase underline"
                  style={{ color: "#6A0DAD" }}
                  data-testid="payment-try-again"
                >
                  Try again
                </button>
              </div>
            )}

            {/* FIX 2 — Single Razorpay primary button */}
            <div className="flex justify-center mt-5">
              <button
                type="button"
                onClick={handleRazorpayCheckout}
                data-testid="pay-razorpay"
                style={{
                  background: "#6A0DAD",
                  color: "#FFFFFF",
                  minHeight: "52px",
                  padding: "0 28px",
                  borderRadius: "14px",
                  fontFamily: "'Cinzel', serif",
                  fontSize: "16px",
                  letterSpacing: "1px",
                  border: "none",
                  cursor: "pointer",
                  width: "100%",
                  maxWidth: "460px",
                  fontWeight: 600,
                  boxShadow: "0 8px 22px rgba(106,13,173,0.28)",
                  transition: "transform .15s ease, box-shadow .2s ease",
                }}
                onMouseOver={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; }}
                onMouseOut={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
              >
                Pay Securely &mdash; Rs {PRODUCT.price} &rarr;
              </button>
            </div>

            {/* FIX 5 — reserved-hold urgency line BELOW the pay button */}
            <p
              className="text-center mt-3"
              style={{ color: "#9e94ae", fontSize: "11px" }}
              data-testid="reserved-hold-line"
            >
              <span aria-hidden>&#128274;</span> Your prescription is reserved for 24 hours
            </p>

            {/* FIX 2 — Trust section: Razorpay Secured + SSL + payment logos + 7-day refund */}
            <div className="mt-8" data-testid="trust-section">
              <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-[#6B5E7E]">
                <span className="inline-flex items-center gap-1.5">
                  <span aria-hidden style={{ color: "#3395FF", fontWeight: 700, letterSpacing: "0.5px" }}>Razorpay</span>
                  <span>Secured</span>
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span aria-hidden style={{ color: "#D4A855" }}>&#128274;</span>
                  <span>100% Secure Payments &middot; 256-bit SSL Encrypted</span>
                </span>
              </div>

              <div className="mt-4 flex items-center justify-center gap-3 flex-wrap opacity-70">
                {["UPI", "GPay", "PhonePe", "Paytm", "Visa", "Mastercard", "RuPay"].map((label) => (
                  <span
                    key={label}
                    className="inline-flex items-center justify-center px-2.5 h-6 rounded-md border text-[10px] font-semibold tracking-wide bg-white"
                    style={{ borderColor: "#d8cfe4", color: "#6B5E7E", filter: "grayscale(1)" }}
                  >
                    {label}
                  </span>
                ))}
              </div>

              <p className="mt-4 text-center text-xs" style={{ color: "#6B5E7E" }}>
                <span aria-hidden style={{ color: "#D4A855" }}>&#10084;</span>{" "}
                7-Day Full Refund Promise
              </p>
            </div>

            <p className="text-center text-xs text-[#6B5E7E] mt-7">Your full prescription will be sent to your WhatsApp and email after payment is confirmed.</p>
          </div>
        </section>
      </div>

      {/* FIX 4 — Sticky bottom Unlock CTA (only until payment is confirmed) */}
      <div
        role="region"
        aria-label="Unlock prescription"
        data-testid="sticky-unlock-bar"
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 50,
          background: "#6A0DAD",
          borderTop: "1px solid #D4A855",
          boxShadow: "0 -6px 22px rgba(44,10,94,0.25)",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        <button
          type="button"
          onClick={handleRazorpayCheckout}
          data-testid="sticky-unlock-cta"
          style={{
            display: "block",
            width: "100%",
            maxWidth: "400px",
            margin: "0 auto",
            padding: "14px 20px",
            background: "transparent",
            color: "#FFFFFF",
            fontFamily: "'Cinzel', serif",
            fontSize: "15px",
            letterSpacing: "1px",
            fontWeight: 600,
            border: "none",
            cursor: "pointer",
            textAlign: "center",
          }}
        >
          <span aria-hidden style={{ color: "#D4A855", marginRight: "8px" }}>&#10022;</span>
          Unlock My Full Prescription &mdash; Rs {PRODUCT.price} &rarr;
        </button>
      </div>

      {/* Bottom-of-page spacer so sticky bar never covers final text */}
      <div style={{ height: "84px" }} aria-hidden />
    </div>
  );
}

function SectionTitle({ eyebrow, title }) {
  return (
    <div>
      <div className="ph-overline">{eyebrow}</div>
      <h2 className="font-serif text-3xl md:text-4xl text-[#2C0A5E] mt-2">{title}</h2>
    </div>
  );
}
